import EquipmentStatus from '@/pages/Equipment/components/Common/EquipmentStatus'
import EquipmentOrganizationsText from '@/pages/Equipment/components/Common/EquipmentOrganizationsText'
import SeparateLine from '@/shared/Forms/SeparateLine'
import css from '@/pages/Equipment/components/SidePages/EquipmentSidePage/equipmentSidePage.module.css'
import timeUtils from '@/utils/time.utils.ts'
import EquipmentModelNameSpan from '@/pages/Equipment/components/Common/EquipmentModelNameSpan'
import EquipmentProtocolNameSpan from '@/pages/Equipment/components/Common/EquipmentProtocolNameSpan'
import FormEditButton from '@/shared/Buttons/FormEditButton'
import FormDeleteButton from '@/shared/Buttons/FormDeleteButton'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { IPassportGroup } from '@/pages/MDM/mdm.types.ts'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'
import EquipmentPassportTree from '@/pages/Equipment/components/SidePages/EquipmentFormFields/EquipmentPassportTree'
import EquipmentPassportInstanceValuesTable from '@/pages/Equipment/components/SidePages/EquipmentFormFields/EquipmentPassportInstanceValuesTable'
import { IEquipment, IEquipmentPassport } from '@/pages/Equipment/equipment.types.ts'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import equipmentApi from '@/pages/Equipment/equipment.api.ts'
import equipmentEvents from '@/pages/Equipment/events/equipment/equipment.events.ts'
import ProtectedContent from '@/services/rights/ProtectedContent'
import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import domUtils from '@/utils/dom.utils'
import GoToLinkButton from '@/shared/Buttons/GoToLinkButton'
import urls from '@/navigation/urls.ts'
import equipmentUtils from '@/pages/Equipment/equipment.utils'
import userUtils from '@/pages/User/user.utils'

interface IProps {
  onClose: () => void
  onEdit: () => void
  equipment: IEquipment
  passport: IEquipmentPassport
}

const EquipmentSidePageViewContent = (props: IProps) => {
  const groupSelectCtrl = useTreeSelectCtrl<IPassportGroup['id']>(`/settings/equipment/view`, null, 'regularState')
  const tableRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const responsibleUsersText = props.equipment.responsibleUsers
    .map(({ user }) => {
      if (user.userInfo) return userUtils.getFullName(user.userInfo)
      return user.name
    })
    .filter(Boolean)
    .join(', ')

  const handleRemove = () => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление лифта по адресу:\n${equipmentUtils.getEquipmentAddress(props.equipment.location)}`,
      buttonType: 'primary',
      onConfirm: () =>
        equipmentApi
          .deleteEquipment(props.equipment.id)
          .then((r) => r.status === 'success' && equipmentEvents.deleted(r.body.id))
          .then(() => props.onClose()),
    })
  }

  const pushGroupIdToUrlHash = (groupId: IPassportGroup['id']) => {
    location.hash = `#group-${groupId}`
    navigate(location, { replace: true })
  }

  const handleGroupSelect = (groupId: IPassportGroup['id']) => {
    const targetElement = tableRef.current?.querySelector(`#groupId_${groupId}`) as HTMLElement
    domUtils.scrollToTargetElement(targetElement)
    pushGroupIdToUrlHash(groupId)
    groupSelectCtrl.handleSelect(groupId)
  }

  const renderConnectionConfiguration = () => {
    switch (props.equipment.protocolId) {
      case 'lp-lift':
        return (
          <>
            <p className="text-nm text-secondary">ID контроллера:</p>
            <p className="text-nm">{props.equipment.connectionConfiguration.controllerId}</p>
          </>
        )
      default:
        return null
    }
  }

  const renderPassport = () => {
    if (!props.equipment.passportInstanceId) {
      return <EmptyPlaceholder inscription="Пасспорт лифта не заполнен" />
    }

    if (!props.passport) {
      return (
        <div className="flex-lines width100 center" style={{ height: '150px' }}>
          <ThreeDotsLoader inscription="Загружаем пасспорт... " />
        </div>
      )
    }

    return (
      <div className="inline-flex-gap gap24 width100" style={{ flex: 1, minHeight: 0 }}>
        <div
          style={{
            width: '280px',
            borderRight: '1px solid var(--border-secondary-color)',
            paddingRight: '12px',
          }}
        >
          <EquipmentPassportTree
            passportSchemaId={props.passport.passportId}
            selectCtrl={groupSelectCtrl}
            handleSelect={handleGroupSelect}
          />
        </div>
        <EquipmentPassportInstanceValuesTable
          ref={tableRef}
          passportInstance={props.passport}
          onGroupIntoViewportChange={(activeGroupId) => {
            groupSelectCtrl.handleSelect(activeGroupId)
            pushGroupIdToUrlHash(activeGroupId)
          }}
        />
      </div>
    )
  }

  return (
    <div className="flex-lines height100 gap16">
      <div className="flex-lines gap12">
        <div className="inline-flex-gap gap8 center">
          <EquipmentStatus equipment={props.equipment} />
        </div>
        <h4 className="text-heading-4" style={{ width: '85%' }}>
          {equipmentUtils.getEquipmentAddress(props.equipment.location)}
        </h4>
        <div style={{ width: 'fit-content' }}>
          <GoToLinkButton to={urls.liftScreen(props.equipment.id)} text="Перейти на страницу лифта" />
        </div>
      </div>
      <SeparateLine />
      <div className={css.sidePageGrid}>
        <p className="text-nm text-secondary">Дата создания:</p>
        <p className="text-nm">{timeUtils.formatDate(new Date(props.equipment.createdAt), 'dd MMM yyyy, HH:mm')}</p>
        <p className="text-nm text-secondary">Дата старта обслуживания:</p>
        <p className="text-nm">
          {props.equipment.maintenanceStartDate
            ? timeUtils.formatDate(new Date(props.equipment.maintenanceStartDate), 'dd MMM yyyy')
            : '-'}
        </p>
        <p className="text-nm text-secondary">Обслуживающая организация:</p>
        <EquipmentOrganizationsText organizations={props.equipment.organizations} />
        <p className="text-nm text-secondary">Модель:</p>
        <EquipmentModelNameSpan modelId={props.equipment.modelId} className="text-nm" />
        <p className="text-nm text-secondary">Протокол:</p>
        <EquipmentProtocolNameSpan protocolId={props.equipment.protocolId} />
        {renderConnectionConfiguration()}
        <p className="text-nm text-secondary">Комментарий:</p>
        <p className="text-nm">{props.equipment.description || '-'}</p>
        <p className="text-nm text-secondary">Ответственные:</p>
        <p className="text-nm">{responsibleUsersText || '-'}</p>
      </div>
      <SeparateLine />
      <h5 className="text-heading-5">Паспорт лифта</h5>
      {renderPassport()}
      <div style={{ marginTop: 'auto' }}>
        <SeparateLine />
      </div>
      <div className="inline-flex-gap" style={{ paddingBottom: '20px' }}>
        <ProtectedContent requiredRight="equipment:update">
          <FormEditButton onClick={props.onEdit} />
        </ProtectedContent>
        <ProtectedContent requiredRight="equipment:delete">
          <FormDeleteButton onClick={handleRemove} />
        </ProtectedContent>
      </div>
    </div>
  )
}

export default EquipmentSidePageViewContent
